import { TreeSelect } from 'antd';
import { UIEvent, useCallback, useMemo } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import {
  useAdditionalFormContext,
  useAutoClearFormFieldValueIfNotInOptions,
  useInfiniteSearchSelect,
} from '../common/form';
import { isPopupScrolledToBottom } from '../common/utils/popupScroll';
import { DataObjectGroupType, DataObjectType } from './types';
import {
  BaseDataObjectGroupDto,
  getDataObjectGroupTreeData,
  getDataObjectGroupsFromOptionPages,
  getSelectedIdsFromTreeValue,
  useInfiniteSearchDataObjectGroupTreesQuery,
} from '../dataObjectGroup';

export interface DataObjectGroupSelectProps {
  dataObjectType: DataObjectType;
  disabled?: boolean;
  name: string;
  isLoading?: boolean;
  defaultOption?: BaseDataObjectGroupDto;
  onChange?: () => void;
  placeholder?: string;
  multiple?: boolean;
}

export function DataObjectGroupAutoComplete({
  dataObjectType,
  disabled,
  name,
  isLoading,
  defaultOption,
  onChange,
  placeholder,
}: DataObjectGroupSelectProps) {
  const value = useWatch({ name });
  const groupType =
    dataObjectType === DataObjectType.IN
      ? DataObjectGroupType.I
      : DataObjectGroupType.O;
  const { data, selectProps } = useInfiniteSearchSelect({
    useSearchHook: useInfiniteSearchDataObjectGroupTreesQuery,
    searchOptions: {
      groupType,
      ...getSelectedIdsFromTreeValue(value),
    },
  });

  const dataObjectGroupsTreeData = useMemo(() => {
    const groups = getDataObjectGroupsFromOptionPages(
      data?.pages,
      defaultOption,
    );

    return getDataObjectGroupTreeData(groups);
  }, [data, defaultOption]);

  useAutoClearFormFieldValueIfNotInOptions({
    name,
    options: dataObjectGroupsTreeData,
    isEnabled: !selectProps.loading,
  });

  const { onChange: formOnChange } = useAdditionalFormContext();
  const { loading: isGroupOptionLoading, onScrollDown } = selectProps;
  const handlePopupScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      if (
        onScrollDown &&
        !isGroupOptionLoading &&
        isPopupScrolledToBottom(target)
      ) {
        void onScrollDown();
      }
    },
    [isGroupOptionLoading, onScrollDown],
  );

  return (
    <Controller
      name={name}
      render={({ field }) => (
        <TreeSelect
          className="treeSelect"
          id={name}
          onChange={(value: any) => {
            field.onChange(value === undefined ? null : value);
            onChange?.();
            formOnChange?.();
          }}
          disabled={disabled}
          showSearch={selectProps.showSearch}
          value={field.value}
          filterTreeNode={false}
          onSearch={selectProps.onSearch}
          onFocus={selectProps.onFocus}
          onPopupScroll={handlePopupScroll}
          styles={{
            popup: {
              root: {
                marginRight: '5px',
                maxHeight: 400,
                overflow: 'auto',
              },
            },
          }}
          placeholder={placeholder}
          allowClear={selectProps.allowClear}
          treeDefaultExpandAll
          treeData={dataObjectGroupsTreeData}
          loading={isLoading || selectProps.loading}
          onClear={() => {
            selectProps.onClear();
          }}
        />
      )}
    />
  );
}
